import React, { useState, useEffect } from 'react';
import { Box, Tabs, Tab, makeStyles, Container, styled } from '@material-ui/core';
import { Picture, BrokerFavoriteListing } from 'models';
import { usePortalInfos, useLanguage } from 'hooks';
import { useLocation } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { orderBy } from 'lodash';
import { getColorContrastRatio } from 'utils';
import moment from 'moment';
import { getCentrisListingPrice } from 'utils/listing';
import Fuse from 'fuse.js';
import { images } from 'assets/images';
import {
  MAX_FILTER_PRICE,
  isDev,
  ENG,
  EN,
  FR,
  COLOR,
  COLOR_CONTRAST_RATIO,
} from 'config/constants';
import {
  ListingsHeader,
  ListingsShowcase,
  ImageCarousel,
  SearchBar,
  CentrisSearchBar,
} from './index';

const useStyles = makeStyles({
  ourListings: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
  },
  tabsContent: {
    marginTop: 20,
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'row',
  },
  tabItem: {
    textTransform: 'none',
  },
  listingsContent: {
    minHeight: 400,
  },
});

interface Props {
  hideHeader?: boolean;
}

function Properties({ hideHeader }) {
  const styles = useStyles();
  const intl = useIntl();
  const Language = useLanguage();
  const isEn = Language === ENG;
  const location = useLocation();
  const dev_domain_name = new URLSearchParams(location.search).get('domain_name');
  const fuseOptions = {
    isCaseSensitive: false,
    // includeScore: false,
    // shouldSort: true,
    // includeMatches: false,
    // findAllMatches: false,
    minMatchCharLength: 3,
    // location: 0,
    threshold: 0.2,
    // distance: 100,
    // useExtendedSearch: false,
    ignoreLocation: false,
    // ignoreFieldNorm: false,
    // fieldNormWeight: 1,
    keys: ['address'],
  };
  const {
    broker,
    broker_portal_infos,
    favorite_listings,
    centris_listings,
    centris_listings_from_db,
    sections,
  } = usePortalInfos(null, window.location.hostname, isEn ? EN : FR);
  const centrisFuseOptions = {
    isCaseSensitive: false,
    minMatchCharLength: 3,
    threshold: 0.2,
    ignoreLocation: false,
    keys: [
      'mls',
      'postalcode',
      'appartement',
      'civic_number_start',
      'civic_number_end',
      'street',
      'municipality.description',
    ],
  };
  const [image_carousel_open, setImageCarouselOpen] = useState(false);
  const [image_carousel_pictures, setImageCarouselPictures] = useState<Picture[]>([]);
  const [selected_tab, setSelectedTab] = useState(0);
  const [centris_filters, setCentrisFilters] = useState({
    price_range: [0, MAX_FILTER_PRICE],
    min_bedrooms: 0,
    min_bathrooms: 0,
    property_type: 'buy',
  });
  const [showcase_listings, setShowcaseListings] = useState<[]>([]);
  const [searchResults, setSearchResults] = useState([]);
  const [selected_filters, setSelectedFilters] = useState({
    drone: false,
    video: false,
    floorplan: false,
    blue_hour: false,
    virtual_staging: false,
  });
  const [sorted_centris_listings, setSortedCentrisListings] = useState<[]>([]);
  const [color_contrast_ratio, setColorContrastRatio] = useState(1);
  const fmtOurListings = intl.formatMessage({ id: 'portal.ourListings' });
  const fmtCentrisListings = intl.formatMessage({ id: 'portal.centrisListings' });

  const secondary_color =
    broker_portal_infos?.template_style?.secondary_color || COLOR.SECONDARY_BLUE;
  const primary_color_contrast_ratio = getColorContrastRatio(
    broker_portal_infos?.template_style?.primary_color,
    COLOR.WHITE,
  );
  const secondary_color_contrast_ratio = getColorContrastRatio(secondary_color, COLOR.WHITE);
  const primary_bg_text_color =
    primary_color_contrast_ratio > COLOR_CONTRAST_RATIO ? COLOR.WHITE : COLOR.BLACK;
  const secondary_bg_text_color =
    secondary_color_contrast_ratio > COLOR_CONTRAST_RATIO ? COLOR.WHITE : COLOR.BLACK;

  useEffect(() => {
    if (broker_portal_infos?.template_style?.primary_color) {
      const ratio = getColorContrastRatio(
        broker_portal_infos?.template_style?.primary_color,
        COLOR.WHITE,
      );
      setColorContrastRatio(ratio);
    }
  }, [sections, broker_portal_infos]);

  useEffect(() => {
    let sorted_centris_listings;
    if (centris_listings.length) {
      sorted_centris_listings = orderBy(
        centris_listings,
        (l) => {
          return moment(l.listing_date);
        },
        ['desc'],
      );
      setShowcaseListings(sorted_centris_listings);
      setSortedCentrisListings(sorted_centris_listings);
      setSelectedTab(0);
    } else {
      setShowcaseListings(favorite_listings);
      setSelectedTab(1);
    }
  }, [centris_listings, favorite_listings]);

  const handleTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    setSelectedTab(newValue);
    if (newValue === 1) {
      setShowcaseListings(favorite_listings);
    } else {
      setShowcaseListings(sorted_centris_listings);
    }
  };

  const handleListingSearch = (inputText: string) => {
    const fuse = new Fuse(favorite_listings, fuseOptions);
    const results = inputText ? fuse.search(inputText) : favorite_listings;
    if (results.length !== 0 && results.length) {
      const filteredListings = results.map((result) => {
        if (result.item) {
          return result.item;
        }
        return result;
      });
      setSearchResults(filteredListings as BrokerFavoriteListing[]);
    } else {
      setSearchResults([]);
    }
  };

  const handleCentrisListingSearch = (inputText: string) => {
    const fuse = new Fuse(showcase_listings, centrisFuseOptions);
    const results = inputText ? fuse.search(inputText) : showcase_listings;
    if (results.length !== 0 && results.length) {
      const filteredListings = results.map((result) => {
        if (result.item) {
          return result.item;
        }
        return result;
      });
      const resultWithFilters = filteredListings.filter((centris_listing: any) => {
        let price_filter =
          getCentrisListingPrice(centris_listing) >= centris_filters.price_range[0] &&
          getCentrisListingPrice(centris_listing) <= centris_filters.price_range[1];
        if (centris_filters.price_range[1] === MAX_FILTER_PRICE) {
          price_filter = getCentrisListingPrice(centris_listing) >= centris_filters.price_range[0];
        }
        let property_type_filter = true;
        if (centris_filters.property_type === 'rent') {
          property_type_filter =
            centris_listing.price === 1 &&
            centris_listing.location_price &&
            centris_listing.rent_price_frequency;
        }
        return (
          centris_listing.bedroom_count >= centris_filters.min_bedrooms &&
          centris_listing.bathroom_count >= centris_filters.min_bathrooms &&
          price_filter &&
          property_type_filter
        );
      });
      setSearchResults(resultWithFilters);
    } else {
      setSearchResults([]);
    }
  };

  return (
    <div>
      {!hideHeader ? (
        <ListingsHeader
          broker={broker}
          slogan={isEn ? broker_portal_infos.slogan_en : broker_portal_infos.slogan_fr}
          bio={isEn ? broker_portal_infos.bio_en : broker_portal_infos.bio_fr}
          profile_picture_url={broker.logo_url || broker.centrisPhotoUrl}
          portal_infos={broker_portal_infos}
          logo_picture_url={broker_portal_infos.logo_image || broker.centrisCompanyLogoUrl || null}
          banner_picture_url={
            broker_portal_infos.banner_image
              ? broker_portal_infos.banner_image
              : images.default_portal_banner
          }
          showcase_listings={
            sorted_centris_listings.length ? sorted_centris_listings : favorite_listings
          }
          centris_listings_from_db={centris_listings_from_db}
          sections={sections}
          showPortalHeader={false}
          favorite_listings={favorite_listings}
          is_white_text={color_contrast_ratio > COLOR_CONTRAST_RATIO}
        />
      ) : null}
      <Container style={{ maxWidth: 1400, marginBottom: 20 }}>
        <Box className={styles.ourListings}>
          {selected_tab === 0 && centris_listings.length > 0 && (
            <CentrisSearchBar
              centris_filters={centris_filters}
              setCentrisFilters={setCentrisFilters}
              setCentrisSearchResults={setSearchResults}
              handleCentrisListingSearch={handleCentrisListingSearch}
              style={broker_portal_infos?.template_style}
              secondary_bg_text_color={secondary_bg_text_color}
            />
          )}
          {selected_tab === 1 && favorite_listings.length > 0 && (
            <SearchBar
              selected_filters={selected_filters}
              setSelectedFilters={setSelectedFilters}
              setSearchResults={setSearchResults}
              handleListingSearch={handleListingSearch}
              style={broker_portal_infos?.template_style}
            />
          )}
        </Box>
        {centris_listings.length > 0 && favorite_listings.length > 0 ? (
          <Box className={styles.tabsContent}>
            <Tabs
              value={selected_tab}
              onChange={handleTabChange}
              TabIndicatorProps={{
                style: {
                  backgroundColor: secondary_color,
                },
              }}
            >
              <Tab
                label={fmtCentrisListings}
                className={styles.tabItem}
                style={{ color: primary_bg_text_color }}
              />
              <Tab
                label={fmtOurListings}
                className={styles.tabItem}
                style={{ color: primary_bg_text_color }}
              />
            </Tabs>
          </Box>
        ) : null}
        <Box className={styles.listingsContent}>
          <ListingsShowcase
            setImageCarouselPictures={setImageCarouselPictures}
            setImageCarouselOpen={setImageCarouselOpen}
            listings={showcase_listings}
            searchResults={searchResults}
            selected_filters={selected_filters}
            centris_filters={centris_filters}
            centris_listings_from_db={centris_listings_from_db}
            style={broker_portal_infos?.template_style}
            is_white_text={secondary_color_contrast_ratio > COLOR_CONTRAST_RATIO}
          />
        </Box>
        {image_carousel_open && image_carousel_pictures.length > 0 ? (
          <ImageCarousel
            setImageCarouselOpen={setImageCarouselOpen}
            pictures={image_carousel_pictures}
            setImageCarouselPictures={setImageCarouselPictures}
          />
        ) : null}
      </Container>
    </div>
  );
}

export default Properties;
